// ============================================================
// useClocks and useBoard - the GM's clocks and the board, with their verbs.
//
// Each is `useShownDocs` over its collection plus the engine's edits
// (`engine/narrative/clocks`, `board`). Every verb is the director's: the
// rules refuse anybody else, and the panels do not offer them.
//
// `useClocks` reports the beats of time a fill stands for (`onBeats`) - a
// clock that marks time passes time as it fills; running that is the
// caller's (`timePasses`) - and the moment a clock becomes full (`onFull`,
// v9.5.9), once per crossing. `tickRound` fills the per-round clocks when
// the combat round moves on.
// ============================================================
import { useCallback, useMemo, useRef } from 'react';
import useShownDocs from './useShownDocs';
import type { ShownSource } from './useShownDocs';
import {
  advanceClock,
  beatsOf,
  becameFull,
  editClock,
  newClock,
  readClock,
  tickRound,
} from '../engine/narrative/clocks';
import type { Clock, ClockPatch } from '../engine/narrative/clocks';
import { editEntry, newEntry, pinnedLine, readEntry } from '../engine/narrative/board';
import type { BoardEntry } from '../engine/narrative/board';

/** The transport, by collection: `multiplayer.onShownUpdate` and its two writers. */
export interface ShownTransport {
  onShownUpdate(name: 'clocks' | 'board', ...rest: Parameters<ShownSource['watch']>): () => void;
  putShown(name: 'clocks' | 'board', ...rest: Parameters<ShownSource['put']>): Promise<void>;
  deleteShown(name: 'clocks' | 'board', ...rest: Parameters<ShownSource['remove']>): Promise<void>;
}

export interface StorySeat {
  sessionId: string | null | undefined;
  uid: string | null;
  isGm: boolean;
  mp: ShownTransport;
}

function useSource(mp: ShownTransport, name: 'clocks' | 'board'): ShownSource {
  return useMemo(
    () => ({
      watch: (...rest) => mp.onShownUpdate(name, ...rest),
      put: (...rest) => mp.putShown(name, ...rest),
      remove: (...rest) => mp.deleteShown(name, ...rest),
    }),
    [mp, name],
  );
}

/**
 * Ids no two things made on this screen share: the clock, and a count. Not
 * `Math.random` - E2E seeds it with the dice, and a stray call shifts every
 * later roll.
 */
function useMint(prefix: string): () => string {
  const count = useRef(0);
  return useCallback(() => `${prefix}-${Date.now().toString(36)}-${(count.current++).toString(36)}`, [prefix]);
}

export interface Clocks {
  clocks: Clock[];
  add(name: string, segments: number): void;
  /** Fill (`by` > 0) or empty (`by` < 0) segments, never past either end. */
  advance(id: string, by: number): void;
  edit(id: string, patch: ClockPatch): void;
  remove(id: string): void;
  /** The round as the directing screen sees it (null = no order running): see `tickRound` in the engine. */
  tickRound(round: number | null): void;
}

type ClockEvents = { onBeats: (beats: number, clock: Clock) => void; onFull: (clock: Clock) => void };

/** Apply one edit to the clock as it stands now, and say what it meant: beats of time, a clock just full. */
function useClockChange(items: Clock[], put: (clock: Clock) => void, events: ClockEvents) {
  const live = useRef({ items, events });
  live.current = { items, events };
  const change = useCallback(
    (id: string, step: (clock: Clock) => Clock) => {
      const before = live.current.items.find((c) => c.id === id);
      const after = before && step(before);
      // A change that changes nothing writes nothing.
      if (!before || !after || after === before) return;
      put(after);
      const beats = beatsOf(before, after);
      if (beats > 0) live.current.events.onBeats(beats, after);
      if (becameFull(before, after)) live.current.events.onFull(after);
    },
    [put],
  );
  const perRound = useCallback(() => live.current.items.filter((clock) => clock.perRound), []);
  return { change, perRound };
}

export function useClocks(
  seat: StorySeat,
  onBeats: ClockEvents['onBeats'],
  onFull: ClockEvents['onFull'] = () => {},
): Clocks {
  const source = useSource(seat.mp, 'clocks');
  const { items, put, remove } = useShownDocs<Clock>({ ...seat, source, read: readClock, label: 'clocks' });
  const mint = useMint('clock');
  const { change, perRound } = useClockChange(items, put, { onBeats, onFull });

  const add = useCallback(
    (name: string, segments: number) => put(newClock({ id: mint(), name, segments, createdAt: Date.now() })),
    [put, mint],
  );
  const advance = useCallback((id: string, by: number) => change(id, (c) => advanceClock(c, by, Date.now())), [change]);
  const edit = useCallback(
    (id: string, patch: ClockPatch) => change(id, (c) => editClock(c, patch, Date.now())),
    [change],
  );
  const tick = useCallback(
    (round: number | null) => perRound().forEach((c) => change(c.id, (clock) => tickRound(clock, round, Date.now()))),
    [change, perRound],
  );
  return { clocks: items, add, advance, edit, remove, tickRound: tick };
}

export interface Board {
  entries: BoardEntry[];
  add(title: string, text: string): void;
  /** Pin a line of the story: shown at once, under no title. */
  pin(line: string): void;
  edit(id: string, patch: Parameters<typeof editEntry>[1]): void;
  remove(id: string): void;
}

export function useBoard(seat: StorySeat): Board {
  const source = useSource(seat.mp, 'board');
  const { items, put, remove } = useShownDocs<BoardEntry>({ ...seat, source, read: readEntry, label: 'board' });
  const mint = useMint('board');
  const live = useRef(items);
  live.current = items;

  /** Write an entry the engine agreed to make; a refusal (nothing to say) writes nothing. */
  const keep = useCallback((entry: BoardEntry | null) => entry && put(entry), [put]);
  const add = useCallback(
    (title: string, text: string) => void keep(newEntry({ id: mint(), title, text, createdAt: Date.now() })),
    [keep, mint],
  );
  const pin = useCallback((line: string) => void keep(pinnedLine(mint(), line, Date.now())), [keep, mint]);
  const edit = useCallback(
    (id: string, patch: Parameters<typeof editEntry>[1]) => {
      const entry = live.current.find((e) => e.id === id);
      if (entry) keep(editEntry(entry, patch));
    },
    [keep],
  );
  return { entries: items, add, pin, edit, remove };
}
